import * as XLSX from 'xlsx';
import { IFormularioAjuste, IItemAjuste } from '../interfaces/AjusteInventario';
import { roundMoney, toLocalDateStringYMD } from './formatters';

const isEmptyUbic = (n: number | null | undefined) => n == null || n === 0;

const padComprobante = (value: number | string | null | undefined, length: number): string => {
    if (value === null || value === undefined || value === '') {
        return ''.padStart(length, '0');
    }
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
    }
    return String(value).padStart(length, '0');
};

const getTipoAjusteLabel = (formulario: IFormularioAjuste): string => {
    const tieneOrigen = !isEmptyUbic(formulario.nroUbicacionOrigen);
    const tieneDestino = !isEmptyUbic(formulario.nroUbicacionDestino);
    if (!tieneOrigen && tieneDestino) return 'Ajuste positivo';
    if (tieneOrigen && !tieneDestino) return 'Ajuste negativo';
    if (tieneOrigen && tieneDestino) return 'Transferencia';
    return 'Ajuste';
};

const getTipoAjusteFileSlug = (formulario: IFormularioAjuste): string => {
    const label = getTipoAjusteLabel(formulario);
    if (label === 'Ajuste positivo') return 'ajuste-positivo';
    if (label === 'Ajuste negativo') return 'ajuste-negativo';
    if (label === 'Transferencia') return 'transferencia';
    return 'ajuste';
};

const formatComprobanteDisplay = (formulario: IFormularioAjuste): string => {
    const letraTitulo = (formulario.letra || '').trim().toUpperCase();
    return `${letraTitulo || '—'}-${padComprobante(formulario.nroSuc, 5)}-${padComprobante(formulario.nroComprobante, 8)}`;
};

export const exportAjusteInventarioExcel = (formulario: IFormularioAjuste, items: IItemAjuste[]): void => {
    const lineas = items.filter((it) => String(it.codigoArticulo || '').trim() !== '');
    if (lineas.length === 0) {
        throw new Error('No hay ítems para exportar');
    }

    const fecha = formulario.fechaEmision
        ? toLocalDateStringYMD(
              formulario.fechaEmision instanceof Date
                  ? formulario.fechaEmision
                  : new Date(formulario.fechaEmision)
          )
        : toLocalDateStringYMD();

    const { pesoTotal, importeTotal } = lineas.reduce(
        (acc, it) => ({
            pesoTotal: acc.pesoTotal + (Number(it.peso) || 0),
            importeTotal: acc.importeTotal + (Number(it.subtotal) || Number(it.cantidad) * Number(it.costo) || 0),
        }),
        { pesoTotal: 0, importeTotal: 0 }
    );

    const sheetRows: Array<Record<string, string | number>> = lineas.map((it, index) => ({
        Item: Number(it.nroItem) || index + 1,
        Código: it.codigoArticulo,
        Descripción: it.descripcion || '',
        Cantidad: Number(it.cantidad) || 0,
        UM: it.um || '',
        Peso: Number(it.peso) || 0,
        Costo: Number(it.costo) || 0,
        SubTotal: Number(it.subtotal) || roundMoney((Number(it.cantidad) || 0) * (Number(it.costo) || 0)),
        Notas: it.notas || '',
    }));

    sheetRows.push({
        Item: '',
        Código: '',
        Descripción: 'Totales',
        Cantidad: '',
        UM: '',
        Peso: Math.round(pesoTotal * 1000) / 1000,
        Costo: '',
        SubTotal: roundMoney(importeTotal),
        Notas: '',
    });

    const wsItems = XLSX.utils.json_to_sheet(sheetRows);
    const wsCabecera = XLSX.utils.aoa_to_sheet([
        ['Tipo', getTipoAjusteLabel(formulario)],
        ['Fecha', fecha],
        ['Comprobante', formatComprobanteDisplay(formulario)],
        ['Sucursal', formulario.nroSuc || ''],
        ['Descripción', formulario.descripcion || ''],
        ['Ubicación origen', formulario.detUbicacionOrigen || formulario.nroUbicacionOrigen || ''],
        ['Ubicación destino', formulario.detUbicacionDestino || formulario.nroUbicacionDestino || ''],
        ['Peso total', Math.round(pesoTotal * 1000) / 1000],
        ['Importe total', roundMoney(importeTotal)],
    ]);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsCabecera, 'Cabecera');
    XLSX.utils.book_append_sheet(wb, wsItems, 'Items');

    const nro = Number(formulario.nroFormulario) > 0 ? formulario.nroFormulario : 'nuevo';
    const tipoFile = getTipoAjusteFileSlug(formulario);
    XLSX.writeFile(wb, `${tipoFile}-${nro}-${fecha}.xlsx`);
};
